import { normaliseGbPostcode } from './postcode.ts'

// Narrow collection contract: allow journey selections, never browser purchases or contact details.
export const CLIENT_EVENTS = ["visit_active", "booking_landed", "product_viewed", "product_changed", "dates_started", "dates_completed", "postcode_entered", "availability_checked", "addons_viewed", "addons_changed", "details_viewed", "details_started", "details_completed", "details_saved", "payment_viewed", "payment_attempted", "confirmation_viewed", "validation_failed", "payment_failed", "client_error", "trustpilot_navigation", "hire_steps_viewed"] as const
export type ClientEventName = typeof CLIENT_EVENTS[number]
export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
export const ATTRIBUTION_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "gbraid", "wbraid"] as const
export const text = (value: unknown, max = 120) => typeof value === "string" ? value.slice(0, max) : ""
export const key = (value: unknown) => text(value, 80).replace(/[^a-zA-Z0-9_.:-]/g, "")
export function safeUrl(value: unknown): string {
  try {
    const url = new URL(String(value))
    if (!["https:", "http:"].includes(url.protocol)) return ""
    const path = url.pathname.split("/").map(part => /@|%40|[0-9a-f]{24,}|(?:cs_|pi_|secret_|token)/i.test(part) ? "redacted" : part).join("/")
    return `${url.origin}${path}`.slice(0, 300)
  } catch { return "" }
}
export function safeDiagnostic(value: unknown, max = 1000): string {
  return text(value, max * 2)
    .replace(/https?:\/\/[^\s)]+/g, match => safeUrl(match))
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]")
    .replace(/\b(?:cs_|pi_|pk_|sk_|whsec_|eyJ)[A-Za-z0-9_.-]+/g, "[token]")
    .replace(/\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/gi, "[postcode]")
    .replace(/\+?\d[\d ()-]{7,}\d/g, "[number]")
    .slice(0, max)
}
export function safeAttribution(value: unknown): Record<string, string> {
  const input = value && typeof value === "object" ? value as Record<string, unknown> : {}
  const result: Record<string, string> = {}
  for (const name of ATTRIBUTION_KEYS) {
    const candidate = text(input[name], 160)
    if (candidate && !/@|%40/i.test(candidate)) result[name] = candidate.replace(/[<>\r\n]/g, "")
  }
  for (const name of ["landing_page", "referrer"]) if (input[name]) result[name] = safeUrl(input[name])
  if (UUID_PATTERN.test(String(input.visit_id))) result.visit_id = String(input.visit_id)
  for (const name of ["ga_client_id", "ga_session_id"]) {
    if (/^\d{1,20}(?:\.\d{1,20})?$/.test(String(input[name]))) result[name] = String(input[name])
  }
  if (input.analytics_variant) result.analytics_variant = key(input.analytics_variant)
  return result
}
export function sanitizeBatch(body: Record<string, unknown>, environment: string) {
  const v = body.visit as Record<string, unknown> | undefined
  if (!v || !UUID_PATTERN.test(String(v.id)) || !Array.isArray(body.events) || body.events.length < 1 || body.events.length > 20) throw new Error("INVALID_ACTIVITY")
  const visit: Record<string, unknown> = {
    id: v.id, environment, entry_type: key(v.entry_type), entry_sku: key(v.entry_sku),
    device: ["mobile", "tablet", "desktop"].includes(String(v.device)) ? v.device : "unknown",
    ...safeAttribution(v),
  }
  const events = body.events.map((raw: unknown) => {
    const e = raw as Record<string, unknown>
    if (!e || !UUID_PATTERN.test(String(e.id)) || !CLIENT_EVENTS.includes(e.name as ClientEventName)) throw new Error("INVALID_ACTIVITY")
    const p = e.properties && typeof e.properties === "object" ? e.properties as Record<string, unknown> : {}
    const properties: Record<string, unknown> = {}
    if (e.name === "postcode_entered") {
      try { properties.postcode = normaliseGbPostcode(p.postcode).postcode } catch { /* Missing or invalid selection. */ }
    }
    if (e.name === "dates_started" || e.name === "dates_completed") {
      for (const name of ["delivery_date", "collection_date"]) {
        const value = p[name]
        if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) continue
        const date = new Date(`${value}T00:00:00Z`)
        if (Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value) properties[name] = value
      }
    }
    for (const name of ["result", "code", "field", "payment_method", "addon_slug"]) if (p[name]) properties[name] = key(p[name])
    for (const name of ["quantity", "addon_count"]) if (Number.isInteger(p[name]) && Number(p[name]) >= 0 && Number(p[name]) <= 100) properties[name] = p[name]
    if (UUID_PATTERN.test(String(p.request_id))) properties.request_id = p.request_id
    if (e.name === "client_error") {
      properties.message = safeDiagnostic(p.message, 300)
      properties.stack = safeDiagnostic(p.stack)
    }
    const timestamp = new Date(String(e.at))
    return { id: e.id, name: e.name, at: Number.isFinite(timestamp.getTime()) && Math.abs(Date.now() - timestamp.getTime()) < 24 * 3600_000 ? timestamp.toISOString() : new Date().toISOString(), stage: key(e.stage), product_type: key(e.product_type), sku: key(e.sku), release: key(e.release), properties }
  })
  return { visit, events }
}
export async function reportServerError(db: any, context: { requestId?: string; visitId?: string; code: string; route: string; release?: string; environment?: string; error?: unknown; stage?: string }) {
  try {
    await db.rpc("record_activity_error", {
      p_visit_id: UUID_PATTERN.test(context.visitId ?? "") ? context.visitId : null,
      p_request_id: context.requestId ?? null,
      p_code: key(context.code) || "INTERNAL_ERROR", p_route: context.route.replace(/\/v1\/products\/.*/, "/v1/products/:slug"),
      p_stack: context.error instanceof Error ? safeDiagnostic(context.error.stack?.split('\n').slice(1).join('\n')) : '',
      p_stage: key(context.stage), p_release: key(context.release), p_environment: context.environment ?? "production",
    })
  } catch { /* Reporting must never replace the original failure. */ }
}
